import {useEffect, useState, useRef, useCallback} from 'react'
import {Box, ClickAwayListener, Grid, MenuItem} from '@mui/material'
import LineChart from '../charts/lineChart'
import {useLongitudinalStates} from '../../store/hooks/longitudinalStates'
import {useSdohFilter} from '../../store/hooks/longitudinalSdoh'
import {useQueryCountyState, useQueryAllCounties} from '../../store/hooks/longitudinalStateCounty'
import {useStateChartProvider} from '../../store/hooks/stateChart'
import {useCountyChartProvider} from '../../store/hooks/countyChart'
import {useNationChartProvider} from '../../store/hooks/nationChart'
import ChartDataRefetchOnError from '../common/ChartDataRefetchOnError'
import ChartBlankError from '../common/ChartBlankError'
import ChartLoadingIndicator from '../common/ChartLoadingIndicator'
import ChartTitle from '../common/ChartTitle'
import {StyledSelect, StyledFormControl, StyledMenuItem} from '../RiskOverTimeComponent/RiskOverTimeComponentStyle'
import {POPULATION_COLOR, RACE_AND_ETHNICITY, GENDER, CHART_NAMES} from '../Config/config'
import {generateLabels, generateData, checkIsChartResponseEmpty, generateAriaLabelForChart} from '../common/helpers'
import {ChartActionButtons} from '../common/ChartActionButtons'
import {useTableViewContext} from '../../store/hooks/tableView'
import TimeSeriesTable from '../common/TimeSeriesTable'
import theme from '../../themes/pTheme/pTheme'
import ToggleButton from '../common/ToggleButton'
import GraphTooltip from '../charts/GraphTooltip'
import MarkerTooltip from '../charts/MarkerTooltip/MarkerTooltip'
import {handleClickChartOutside} from '../common/markerHelpers'

const chartData = new Array(24).fill(null)
const titleInit = 'Adult Population Over Time'
const dataSetInit = [
  {
    label: '',
    data: new Array(24).fill(null),
    borderColor: theme.palette.hpWhite,
    backgroundColor: theme.palette.hpWhite,
    spanGaps: true
  },
  {
    label: '',
    data: new Array(24).fill(null),
    borderColor: theme.palette.hpWhite,
    backgroundColor: theme.palette.hpWhite,
    spanGaps: true
  }
]

const PopulationOverTimeComponent = () => {
  const populationChartRef = useRef()
  const [zoomProps, setZoomProps] = useState({reset: true, in: false, out: true})
  const [clickAwayEnabled, setClickAwayEnabled] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [showMarkerTooltip, setShowMarkerTooltip] = useState(false)
  const [selectedState, setSelectedState] = useState('')
  const [selectedAllState, setSelectedAllState] = useState('')
  const [selectedCounty, setSelectedCounty] = useState('')
  const [selectedSdohFilter, setSelectedSdohFilter] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const {states, statesList, updateDefaultState, updateDefaultChartType, updateDefaultCounties} =
    useLongitudinalStates()
  const [queryCounty, {county}] = useQueryCountyState()
  const [queryAllCounty, {allCounty}] = useQueryAllCounties()
  const [updatedStates, setUpdatedStates] = useState([])
  const {sdohFilter} = useSdohFilter()
  const [title, setTitle] = useState(titleInit)
  const [isError, setIsError] = useState(false)
  const [yMax, setYMax] = useState(0)
  const [yMin, setYMin] = useState(0)
  const [xLabels, setXLabels] = useState([])
  const [xDates, setXDates] = useState([])
  const [dataSets, setDataSets] = useState(dataSetInit)
  const [displayLegend, setDisplayLegend] = useState(false)
  const [chartArialabel, setChartArialabel] = useState('Population Over Time Chart per 100k')
  const [currentSelections, setCurrentSelections] = useState({})
  const {getChartStateData, chartStateData, setStateChartVariables} = useStateChartProvider()
  const {getChartNationData, chartNationData, setNationChartVariables} = useNationChartProvider()
  const {getChartCountyData, chartCountyData, setCountyChartVariables} = useCountyChartProvider()
  const {showPopulationOverTimeTable, updateTogglePopulationOverTime} = useTableViewContext()
  const [openStateDropdown, setOpenStateDropdown] = useState(false)
  const [openCountyDropdown, setOpenCountyDropdown] = useState(false)
  const [openSdohDropdown, setOpenSdohDropdown] = useState(false)

  const calculateLegend = useCallback(
    (name) => {
      let str1 = name.replaceAll(`${selectedSdohFilter}_BIN_`, '')
      str1 = str1.replaceAll('_COUNT_PER_100K', '')
      str1 = str1.replaceAll('_', ' - ')
      if (selectedSdohFilter === 'INCOME') {
        str1 = str1.replaceAll(' - TO - ', ' - ')
        const str = str1.split(' - ')
        str1 = `$${str[0]} - $${str[1]}`
        str1 = str1.replaceAll('K', 'k')
        str1 = str1.replaceAll('$UNDER -', 'Under')
        str1 = str1.replaceAll('- $PLUS', '+')
      }
      str1 = str1.replaceAll(' - PLUS', '+')
      if (selectedSdohFilter === 'RACE') {
        const str = RACE_AND_ETHNICITY?.find((item) => item.key === str1)
        return str?.name
      }
      if (selectedSdohFilter === 'GENDER') {
        const str = GENDER?.find((item) => item.key === str1)
        return str?.name
      }
      return str1
    },
    [selectedSdohFilter]
  )

  const separateData = (data) => {
    const uniqeLabel = []
    for (const obj of data || []) {
      if (!uniqeLabel.includes(obj.option_name)) uniqeLabel.push(obj.option_name)
    }
    const newData = []
    for (const index in uniqeLabel) {
      const newObj = []
      for (const obj of data || []) {
        if (obj.option_name === uniqeLabel[index]) newObj.push(obj)
      }
      newData.push(newObj)
    }
    return newData
  }

  const generateDataSet = useCallback(() => {
    let data = []
    if (selectedState === 'EUS' && selectedCounty === '' && selectedSdohFilter !== '') {
      data = chartNationData?.chartData?.sdohPopulation
    } else if (selectedState !== '' && selectedCounty === '' && selectedSdohFilter !== '') {
      data = chartStateData?.chartData?.sdohPopulation
    }
    if (selectedState !== '' && selectedCounty !== '' && selectedSdohFilter !== '') {
      data = chartCountyData?.chartData?.sdohPopulation
    }
    const dataSets = []

    const newData = separateData(data)
    const [xLables, xLablesWithDates, yMin, yMax] = generateLabels(newData, 'option_count_per_100k')
    for (const [i, obj] of (newData || []).entries()) {
      const set = {
        label: calculateLegend(obj[0].option_name),
        data: obj && obj?.length > 0 ? generateData(obj, xLables, 'option_count_per_100k') : chartData,
        borderColor: POPULATION_COLOR[i],
        borderWidth: 3,
        backgroundColor: POPULATION_COLOR[i],
        spanGaps: true
      }
      dataSets.push(set)
    }

    if (data?.length === 0 && selectedSdohFilter !== '')
      dataSets.push({
        label: selectedSdohFilter,
        data: chartData,
        borderColor: POPULATION_COLOR[0],
        borderWidth: 3,
        backgroundColor: POPULATION_COLOR[0],
        spanGaps: true
      })
    dataSets.length > 0 && setDataSets(dataSets)
    setXLabels(xLables)
    setYMax(yMax)
    setYMin(yMin)
    setXDates(xLablesWithDates)
  }, [
    selectedState,
    selectedCounty,
    selectedSdohFilter,
    chartNationData?.chartData?.sdohPopulation,
    chartStateData?.chartData?.sdohPopulation,
    chartCountyData?.chartData?.sdohPopulation,
    calculateLegend
  ])

  const handleChangeState = (event) => {
    const newState = event.target.value
    setSelectedCounty('')
    setSelectedState(newState)
    updateDefaultChartType('PopulationOverTimeChart')
    updateDefaultState('PopulationOverTimeChart', newState)
    if (newState === statesList[0].entireUnitedStateAbbrevationLabel) {
      setSelectedAllState(false)
    } else {
      setSelectedAllState(newState)
    }
  }

  const handleChangeCounty = (event) => {
    const newCounty = event.target.value
    setSelectedCounty(newCounty)
  }

  useEffect(() => {
    if (statesList[0].chartType === 'PopulationOverTimeChart' && county?.longitudinalCountyList?.length > 0) {
      updateDefaultCounties(statesList[0].chartType, county || allCounty)
    }
    if (statesList[0].chartType === 'RiskOverTimeChart' && county?.longitudinalCountyList?.length > 0) {
      updateDefaultCounties(statesList[0].chartType, county)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesList, county, allCounty])

  useEffect(() => {
    if (states?.longitudinalStateList?.length > 0) {
      const tempStates = [...states.longitudinalStateList]
      tempStates.unshift({
        STATE_NAME: statesList[0].entireUnitedStateLabel,
        STATE_ABBREVIATION: statesList[0].entireUnitedStateAbbrevationLabel
      })
      setUpdatedStates(tempStates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states])

  useEffect(() => {
    if (selectedState) {
      if (selectedState === statesList[0].entireUnitedStateAbbrevationLabel) {
        queryAllCounty()
      } else {
        queryCounty({variables: {state: selectedState}})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState])

  const handleChangeSdohFilter = (event, item) => {
    setSelectedSdohFilter(event.target.value)
    const filter = item.props.children
    setTitle(`${filter}  Over Time`)
  }

  const fetchChartData = () => {
    const updatedVariables = {
      sdohKey: selectedSdohFilter,
      stateCode: selectedState,
      stateCountyCode: selectedCounty
    }
    if (selectedState === 'EUS' && selectedCounty === '' && selectedSdohFilter !== '') {
      setNationChartVariables(updatedVariables)
      getChartNationData()
    } else if (selectedState !== '' && selectedCounty === '' && selectedSdohFilter !== '') {
      setStateChartVariables(updatedVariables)
      getChartStateData()
    } else if (selectedState !== '' && selectedCounty !== '' && selectedSdohFilter !== '') {
      setCountyChartVariables(updatedVariables)
      getChartCountyData()
    }
    if (selectedSdohFilter !== '') setDisplayLegend(true)
  }

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 260
      }
    }
  }

  useEffect(() => {
    fetchChartData()

    if (selectedSdohFilter !== '') {
      const {STATE_NAME: stateName} = updatedStates?.find((o) => o.STATE_ABBREVIATION === selectedState) ?? {}
      let {COUNTY_NAME: countyName} =
        county?.longitudinalCountyList.find((o) => o.STATE_COUNTY_FIPS_CODE === selectedCounty) ?? {}
      const {SDOH_NAME: sdohName} =
        sdohFilter?.longitudinalSdohList.find((o) => o.SDOH_KEY === selectedSdohFilter) ?? {}
      if (!countyName) countyName = 'All Counties'
      setCurrentSelections({state_name: stateName, county_name: countyName, sdoh_name: sdohName})
      setChartArialabel(() => generateAriaLabelForChart(sdohName, countyName, stateName))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, selectedCounty, selectedSdohFilter])

  useEffect(() => {
    setIsLoading(() => chartStateData.loadingChart || chartNationData.loadingChart || chartCountyData.loadingChart)
  }, [chartStateData.loadingChart, chartNationData.loadingChart, chartCountyData.loadingChart])
  useEffect(() => {
    if (isLoading) setShowLoader(isLoading)
    else generateDataSet()
  }, [isLoading, generateDataSet])

  useEffect(() => {
    setIsError(
      () => chartStateData.errorLoadingChart || chartNationData.errorLoadingChart || chartCountyData.errorLoadingChart
    )
  }, [chartStateData.errorLoadingChart, chartNationData.errorLoadingChart, chartCountyData.errorLoadingChart])

  const handelTooltip = (data) => {
    const tooltip = []
    for (const dataItem of data) {
      const title = dataItem.dataset.label
      let RatePer100K = dataItem.formattedValue
      RatePer100K = RatePer100K.replaceAll(/\.(\d*)/g, '')
      const date = xDates[dataItem.dataIndex]
      tooltip.push([{[title]: ''}, {'Rate Per 100K': RatePer100K}, {Date: date}])
    }
    return tooltip
  }
  const conditionalRender = () => {
    if (!isError && !showLoader && !checkIsChartResponseEmpty(dataSets)) {
      if (showPopulationOverTimeTable)
        return (
          <TimeSeriesTable
            dataSets={dataSets}
            xDates={xDates}
            displayLegend={displayLegend}
            currentSelections={currentSelections}
          />
        )
      else
        return (
          <Box sx={{height: '100%'}} aria-label={chartArialabel} position='relative'>
            <ClickAwayListener
              mouseEvent={clickAwayEnabled ? 'onClick' : false}
              onClickAway={() => handleClickChartOutside(populationChartRef.current)}
              touchEvent={clickAwayEnabled ? 'onTouchEnd' : false}
            >
              <Box>
                <LineChart
                  dataSets={dataSets}
                  yMax={yMax}
                  yMin={yMin}
                  chartRef={populationChartRef}
                  xLabels={xLabels}
                  setZoomProps={setZoomProps}
                  displayLegend={displayLegend}
                  handelTooltip={handelTooltip}
                  tooltipPosition='top'
                  name={CHART_NAMES.POPULATION}
                  setTooltipVisible={setTooltipVisible}
                  setShowMarkerTooltip={setShowMarkerTooltip}
                  setClickAwayEnabled={setClickAwayEnabled}
                />
              </Box>
            </ClickAwayListener>
            {populationChartRef?.current?.position && (
              <GraphTooltip
                chartRef={populationChartRef}
                visibility={tooltipVisible}
                handelTooltip={handelTooltip}
                setTooltipVisible={setTooltipVisible}
              />
            )}
            {showMarkerTooltip && (
              <MarkerTooltip
                chartRef={populationChartRef.current}
                name={CHART_NAMES.POPULATION}
                setClickAwayEnabled={setClickAwayEnabled}
              />
            )}
            <Box display={'flex'} justifyContent={'space-between'}>
              <Box visibility={displayLegend ? '' : 'hidden'} width={{md: '65%', sm: '55%', xs: '55%'}}>
                <div id={`legend-container-${CHART_NAMES.POPULATION}`} />
              </Box>
              <ChartActionButtons
                displayLegend={displayLegend}
                zoomProps={zoomProps}
                chartRef={populationChartRef}
                setClickAwayEnabled={setClickAwayEnabled}
              />
            </Box>
          </Box>
        )
    }
  }

  const handleStateDropdownClose = () => {
    setOpenStateDropdown(false)
  }

  const handleStateDropdownOpen = () => {
    setOpenStateDropdown(true)
  }

  const handleCountyDropdownClose = () => {
    setOpenCountyDropdown(false)
  }

  const handleCountyDropdownOpen = () => {
    setOpenCountyDropdown(true)
  }
  const handleSdohDropdownClose = () => {
    setOpenSdohDropdown(false)
  }

  const handleSdohDropdownOpen = () => {
    setOpenSdohDropdown(true)
  }

  const handleGetFilterNameFromKey = (list, filterValue, key, filterValueName) => {
    if (!filterValue) {
      if (filterValueName === 'COUNTY_NAME') {
        return 'All Counties'
      }
      return 'Default'
    }
    return list?.find((item) => item[key] === filterValue)?.[filterValueName]
  }

  const stateName = handleGetFilterNameFromKey(updatedStates, selectedState, 'STATE_ABBREVIATION', 'STATE_NAME')
  const countyName = handleGetFilterNameFromKey(
    statesList[0].currentRiskOverTimeCounties?.longitudinalCountyList,
    selectedCounty,
    'STATE_COUNTY_FIPS_CODE',
    'COUNTY_NAME'
  )
  const sdohFilterName = handleGetFilterNameFromKey(
    sdohFilter?.longitudinalSdohList,
    selectedSdohFilter,
    'SDOH_KEY',
    'SDOH_NAME'
  )

  return (
    <Box position='relative'>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <ChartTitle title={title} showInfo={false} />
      </div>
      <Grid container spacing={2}>
        <StyledFormControl sx={{m: 2}} data-testid='population-chart-state-dropdown'>
          <StyledSelect
            value={selectedState}
            onChange={handleChangeState}
            displayEmpty
            open={openStateDropdown}
            onClose={handleStateDropdownClose}
            onOpen={handleStateDropdownOpen}
            inputProps={{
              'aria-label': openStateDropdown
                ? 'population-chart-state-dropdown-expanded'
                : `population-chart-state-selected-${stateName}`,
              'aria-labelledby': 'population-state',
              id: 'population-state'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                return <span aria-label='state-select-label'>Select a state</span>
              }
              const state = updatedStates?.find((state) => state.STATE_ABBREVIATION === selected)
              if (state) {
                return (
                  <span key={state.STATE_ABBREVIATION} aria-label='state-select-label'>
                    {state.STATE_NAME}
                  </span>
                )
              }
              return null
            }}
          >
            {updatedStates?.map((state, index) => (
              <StyledMenuItem
                key={state.STATE_ABBREVIATION}
                value={state.STATE_ABBREVIATION}
                className={index === 0 ? 'sticky' : ''}
              >
                {state.STATE_NAME}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        <StyledFormControl
          sx={{m: 2}}
          disabled={!selectedAllState || selectedState === ''}
          data-testid='population-chart-county-dropdown'
        >
          <StyledSelect
            value={selectedCounty}
            onChange={handleChangeCounty}
            displayEmpty
            open={openCountyDropdown}
            onClose={handleCountyDropdownClose}
            onOpen={handleCountyDropdownOpen}
            inputProps={{
              'aria-label': openCountyDropdown
                ? 'population-chart-county-dropdown-expanded'
                : `population-chart-county-selected-${countyName}`,
              'aria-labelledby': 'population-county',
              id: 'population-county'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                if (selectedState !== '') return <span aria-label='population-chart-All-Counties'>All Counties</span>
                return <span aria-label='county-select-label'>Select a county</span>
              }
              const county = statesList[0].currentPOTCounties?.longitudinalCountyList?.find(
                (county) => county.STATE_COUNTY_FIPS_CODE === selected
              )
              return county ? county.COUNTY_NAME : null
            }}
          >
            <MenuItem key='' value=''>
              <span aria-label='population-chart-All-Counties'>All Counties</span>
            </MenuItem>
            {statesList[0].currentPOTCounties?.longitudinalCountyList?.map((county) => (
              <MenuItem key={county.STATE_COUNTY_FIPS_CODE} value={county.STATE_COUNTY_FIPS_CODE}>
                {county.COUNTY_NAME}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        <StyledFormControl sx={{m: 2}} disabled={!selectedState} data-testid='population-chart-sdoh-dropdown'>
          <StyledSelect
            style={{width: '300px'}}
            value={selectedSdohFilter}
            onChange={handleChangeSdohFilter}
            displayEmpty
            open={openSdohDropdown}
            onClose={handleSdohDropdownClose}
            onOpen={handleSdohDropdownOpen}
            inputProps={{
              'aria-label': openSdohDropdown
                ? 'population-chart-social-drivers-of-health-filter-dropdown-expanded'
                : `population-chart-social-drivers-of-health-filter-selected-${sdohFilterName}`,
              'aria-labelledby': 'sdoh-filter',
              id: 'sdoh-filter'
            }}
            MenuProps={MenuProps}
            renderValue={(selected) => {
              if (!selected) {
                return <span aria-label='sdoh-filter-select-label'>Select Community Factor</span>
              }
              const condition = sdohFilter?.longitudinalSdohList?.find((condition) => condition.SDOH_KEY === selected)
              return condition ? condition.SDOH_NAME : null
            }}
          >
            {sdohFilter?.longitudinalSdohList?.map((condition) => (
              <MenuItem key={condition.SDOH_KEY} value={condition.SDOH_KEY}>
                {condition.SDOH_NAME}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
      </Grid>
      <ToggleButton
        toggleBtnOnSelectCallback={updateTogglePopulationOverTime}
        showTable={showPopulationOverTimeTable}
        disableToggleBtn={!displayLegend}
      />
      <Box tabIndex={0}>
        {showLoader && <ChartLoadingIndicator isLoading={isLoading} onLoadComplete={() => setShowLoader(false)} />}
        {selectedSdohFilter && !showLoader && isError && (
          <ChartDataRefetchOnError onTryAgain={() => fetchChartData()} />
        )}

        {selectedSdohFilter && !showLoader && !isError && checkIsChartResponseEmpty(dataSets) && <ChartBlankError />}
        {conditionalRender()}
      </Box>
    </Box>
  )
}

export default PopulationOverTimeComponent

/* 
 This component is responsible for rendering a population over time chart.
 It includes state management for selected filters, chart data fetching, and rendering.

 Function Descriptions:
  - calculateLegend: Formats and returns a readable legend label based on the selected SDOH filter.
  - separateData: Groups data by unique labels for easier processing.
  - generateDataSet: Prepares datasets and chart configurations based on selected filters and chart data.
  - handleChangeState: Updates the selected state and resets related filters.
  - handleChangeCounty: Updates the selected county filter.
  - handleChangeSdohFilter: Updates the selected SDOH filter and chart title.
  - fetchChartData: Fetches chart data based on the selected state, county, and SDOH filter.
  - conditionalRender: Determines and renders the appropriate chart or table view based on the current state.
  - handleStateDropdownClose: Closes the state dropdown menu.
  - handleStateDropdownOpen: Opens the state dropdown menu.
  - handleCountyDropdownClose: Closes the county dropdown menu.
  - handleCountyDropdownOpen: Opens the county dropdown menu.
  - handleSdohDropdownClose: Closes the SDOH filter dropdown menu.
  - handleSdohDropdownOpen: Opens the SDOH filter dropdown menu.
  - handleGetFilterNameFromKey: Retrieves the display name for a selected filter value from a list.
  - handelTooltip: Formats tooltip data for the chart.

*/
